import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { LegalDocument } from '../data/legal';
import { company } from '../data/content';
import { PlusIcon, PhoneIcon, MailIcon } from './icons';
const ease = [0.23, 1, 0.32, 1] as const;
interface LegalModalProps {
  document: LegalDocument;
  onClose: () => void;
}
export function LegalModal({
  document: doc,
  onClose
}: LegalModalProps) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    const previousOverflow = window.document.body.style.overflow;
    window.document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2,
    ease
  }} onClick={onClose} className="fixed inset-0 z-[80] flex items-end justify-center bg-navy-900/85 p-0 sm:items-center sm:p-6" data-id="element-466" data-mp="1hr0vxu8v8fej">
      <motion.div initial={{
      opacity: 0,
      y: 24,
      scale: 0.98
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: 16,
      scale: 0.99
    }} transition={{
      duration: 0.25,
      ease
    }} onClick={event => event.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="legal-title" className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-lg bg-white sm:max-h-[85vh] sm:rounded-lg" data-id="element-467" data-mp="1fnoqp1ydb3jn">
        <div className="flex items-start justify-between gap-6 border-b border-navy-100 px-6 py-5 sm:px-8" data-id="element-468" data-mp="11wjvcc218xf4">
          <div data-id="element-469" data-mp="16q64hu8zmj66">
            <h2 id="legal-title" className="font-display text-xl font-bold text-navy-800" data-id="element-470" data-mp="16qo8xe5t03p6">
              {doc.title}
            </h2>
            <p className="mt-1 text-[13px] text-navy-400" data-id="element-471" data-mp="e7qz0ehi0njz">
              {company.name} · Last updated {doc.updated}
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label={`Close ${doc.title}`} className="-mr-2 -mt-1 shrink-0 rounded-full p-2 text-navy-400 transition-colors duration-150 ease-out hover:bg-navy-50 hover:text-navy-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-400" data-id="element-472" data-mp="65rqi7g4q1un">
            <PlusIcon className="h-5 w-5 rotate-45" data-id="element-473" data-mp="3jemo90y53oyg" />
          </button>
        </div>

        <div className="overflow-y-auto px-6 py-6 sm:px-8" data-id="element-474" data-mp="3egumzgbluqhl">
          <p className="text-[15px] leading-relaxed text-navy-600" data-id="element-475" data-mp="3td3jwb80wrqu">{doc.intro}</p>

          <div className="mt-7 space-y-7" data-id="element-476" data-mp="3odh7g400rfj3">
            {doc.sections.map(section => <section key={section.heading} data-id="element-477" data-mp="3oftugvkmwg30">
                <h3 className="font-display text-[15px] font-bold uppercase tracking-wide text-navy-800" data-id="element-478" data-mp="18la67uoa2x4q">
                  {section.heading}
                </h3>
                <div className="mt-2.5 space-y-3" data-id="element-479" data-mp="1b711zk7tnsfg">
                  {section.body.map(paragraph => <p key={paragraph} className="text-[15px] leading-relaxed text-navy-500" data-id="element-480" data-mp="1g5kn5ield06r">
                      {paragraph}
                    </p>)}
                </div>
              </section>)}
          </div>
        </div>

        <div className="flex flex-col gap-3 border-t border-navy-100 bg-sand px-6 py-4 sm:flex-row sm:items-center sm:px-8" data-id="element-481" data-mp="167y5io5wquj3">
          <p className="text-[13px] text-navy-400" data-id="element-482" data-mp="1ywc538cn9ywr">Questions about this document?</p>
          <div className="flex flex-wrap gap-4 sm:ml-auto" data-id="element-483" data-mp="1yy6i80hocmqa">
            <a href={company.phoneHref} className="inline-flex items-center gap-2 text-sm font-semibold text-sky-600 transition-colors duration-150 ease-out hover:text-sky-500" data-id="element-484" data-mp="1l45stxcm4c2l">
              <PhoneIcon className="h-4 w-4" data-id="element-485" data-mp="2cuts13uq77dm" />
              {company.phoneDisplay}
            </a>
            <a href={company.emailHref} className="inline-flex items-center gap-2 text-sm font-semibold text-sky-600 transition-colors duration-150 ease-out hover:text-sky-500" data-id="element-486" data-mp="2ho4qaglh2ftx">
              <MailIcon className="h-4 w-4" data-id="element-487" data-mp="p1ruhoj3ic0x" />
              Email us
            </a>
          </div>
        </div>
      </motion.div>
    </motion.div>;
}